import React from 'react';
import styled from 'styled-components';
import { useAppSelector, useTranslate } from '@src/core/hooks';
import { useWidgetId } from '@src/core/IdProvider';
import { tariffsState, adderResumeSelector, productSelector, repricingStatusSelector } from '@src/stores/product';
import { LayoutColors } from '@src/core/theme';
import { BodyLarge, BodyRegular } from '@src/fonts/typography/Typography';
import { DarkTextColor } from '@src/fonts/typography/TypographyColors';
import { convertIsoDateToShortDate, formatCents } from '@src/common';
import { SellingMode } from '@src/core/buyerApiClient';
import useSelectedProductId from '@src/hooks/useSelectedProductId';
import useFeatureFlag from '@src/hooks/useFeatureFlag';

interface ProductAdderSummaryProps {
  hideTariffs?: boolean;
  hideResources?: boolean;
}

const ProductAdderSummary = ({ hideTariffs, hideResources }: ProductAdderSummaryProps) => {
  const productId = useSelectedProductId();
  const product = useAppSelector(productSelector(productId));
  const id = useWidgetId();
  const { selectedDate, listOfTariffsPerType } = useAppSelector(tariffsState(id));
  const { isFeatureFlagEnabled } = useFeatureFlag();
  const isServiceFeeEnabled = isFeatureFlagEnabled('PR-647-service-fee');
  const hasSomeServiceFee = listOfTariffsPerType.some(
    (t) => t.selectedQuantity > 0 && t.schedulings?.some((s) => s.tariffGroup?.serviceFee),
  );
  const hasServiceFee = isServiceFeeEnabled && hasSomeServiceFee;
  const { selectedTariffs, selectedResources, resourcesPriceCents, tariffsPriceCents } = useAppSelector(
    adderResumeSelector(id),
  );
  const isRepricing = useAppSelector(repricingStatusSelector(id)) === 'loading';
  const { i18n } = useTranslate();

  const tariffSum = selectedTariffs.reduce((sum, { selectedQuantity }) => sum + selectedQuantity, 0);

  return (
    <>
      {hasServiceFee && product?.sellingMode === SellingMode.WithoutDate ? (
        <ServiceFeeText>{i18n('adder_serviceFee')}</ServiceFeeText>
      ) : null}
      <CombinedSummaryItens>
        <SummaryItem hidden={!selectedDate || product?.sellingMode === SellingMode.WithoutDate}>
          <SummaryLabel>{i18n('adder_selected_date')}</SummaryLabel>
          <SummaryText>{selectedDate ? convertIsoDateToShortDate(selectedDate) : null}</SummaryText>
          {hasServiceFee ? <ServiceFeeText>{i18n('adder_serviceFee')}</ServiceFeeText> : null}
        </SummaryItem>
        <SummaryItem hidden={hideTariffs || !selectedTariffs.length}>
          <SummaryLabel>{i18n('adder_selectedTariff')}</SummaryLabel>
          <SummaryText>
            {i18n('adder_quantity')}:{' '}
            {isRepricing ? i18n('adder_updating_price') : `${tariffSum} - ${formatCents(tariffsPriceCents)}`}
          </SummaryText>
        </SummaryItem>
      </CombinedSummaryItens>
      <SummaryItem hidden={hideResources || !selectedResources.length}>
        <SummaryLabel>{i18n('adder_selectedResources')}</SummaryLabel>
        <SummaryText>
          <span>{`${selectedResources.map((r) => r.resourceSlot.name).join(', ')}`}</span>
          {resourcesPriceCents > 0 && <span>{` - ${formatCents(resourcesPriceCents)}`}</span>}
        </SummaryText>
      </SummaryItem>
    </>
  );
};

const CombinedSummaryItens = styled.div`
  display: flex;
  gap: 16px 143px;
  flex-wrap: wrap;
`;

const SummaryItem = styled.div`
  margin-bottom: 16px;
`;

const SummaryLabel = styled.p`
  ${BodyLarge}
  ${DarkTextColor}
`;

const SummaryText = styled.p`
  ${BodyRegular}
  ${DarkTextColor}
`;

const ServiceFeeText = styled.p`
  ${BodyRegular}
  align-self: flex-start;
  margin-top: 0.25rem;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  background-color: ${LayoutColors.whiteDark};
  color: ${LayoutColors.blackMain};
`;

export default ProductAdderSummary;
